import { TPopupDefaultActions } from '../../services/popup.service.ts';
import { FC, useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import { Popup, TPopupComponent } from '../../popup.abstract.tsx';
import { useTranslation } from 'react-i18next';
import { TextField } from '../../../form/index.ts';
import { Button, Stack, styled } from '@mui/material';
import { DictTranslationLanguagesQueryRes } from '../../../../services/configsData/configsData.service.ts';
import { useBeforeClosePopup } from '../../hooks/useBeforeClosePopup.tsx';
import equal from 'fast-deep-equal/react';
import { TextEditor, TToolbar } from '../../../textEditor/textEditor.component.tsx';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '../../../../../pages/settings/configuration/components/tabs/customer/popups/defaultSettings/components/defaultSettingsForm.component.tsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Theme as theme } from '../../../../styles/theme/theme.ts';
import { Footer } from '../footer.component.tsx';
import { C_Content_Translation_Type } from '../../../../../graphql/generatedModel.ts';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  postprocessTranslations,
  preprocessTranslations,
  localizedLabelForBCP47Tag,
} from './helpers.ts';
import {
  GetWa_AiContentTranslationQueryRes,
  translationService,
} from './service/translation.service.ts';
import { responseHandler } from '../../../../responseHandler/responseHandler.ts';
import { ContentLoading } from '../../../loading/loading.component.tsx';
import { Subscription } from 'rxjs';

export class TranslationPopup extends Popup<ITranslationPopup> {
  Component: FC<TPopupComponent>;

  constructor() {
    super();
    const stream = this.innerStream;

    this.Component = () => {
      const { t, i18n } = useTranslation();

      const SWrapper = useMemo(() => {
        return styled(this.Wrapper)(() => ({
          '& > div > div': { maxWidth: 756 },
        }));
      }, []);

      const [richTextToolbarItems, setRichTextToolbarItems] = useState<TToolbar[] | undefined>(
        undefined,
      );
      const [isRichText, setIsRichText] = useState<boolean>(false);
      const [minRows, setMinRows] = useState<number | undefined>(undefined);
      const [maxLength, setMaxLength] = useState<number | undefined>(undefined);
      const [languages, setLanguages] = useState<DictTranslationLanguagesQueryRes>([]);
      const [translations, setTranslations] = useState<ITranslation[]>([]);
      const [initTranslations, setInitTranslations] = useState<ITranslation[]>([]);
      const [loading, setLoading] = useState(false);
      const [aiUpdateKey, setAiUpdateKey] = useState(0);
      const [isOpened, setIsOpened] = useState(false);

      const aiTranslationsSubRef = useRef<Subscription | null>(null);
      const aiRequestIdRef = useRef<string | null>(null);

      const [originalContent, setOriginalContent] = useState<string>('');
      const [contentType, setContentType] = useState<C_Content_Translation_Type>(
        C_Content_Translation_Type.ARTICLE_DESCRIPTION,
      );

      useBeforeClosePopup({
        stream,
        isDirty: !equal(initTranslations, translations),
        handlers: () => {
          stream.emit('reset', {
            values: { translations: postprocessTranslations(initTranslations) },
          });
        },
        deps: [initTranslations],
      });

      useEffect(() => {
        const openSub = stream.actionListener('open').subscribe((data) => {
          const translationsPreprocessed = preprocessTranslations(
            (data?.languages || []) as DictTranslationLanguagesQueryRes,
            (data?.values?.translations || []) as ITranslation[],
          );

          setInitTranslations(translationsPreprocessed);
          setTranslations(translationsPreprocessed);
          setRichTextToolbarItems(data?.richTextToolbarItems ?? undefined);
          setIsRichText(!!data?.isRichText);
          setMinRows(data?.minRows);
          setMaxLength(data?.maxLength);
          setLanguages((data?.languages || []) as DictTranslationLanguagesQueryRes);
          setOriginalContent(data?.originalContent || '');
          setContentType(data?.contentType || C_Content_Translation_Type.ARTICLE_DESCRIPTION);
          setIsOpened(true);
          setLoading(false);

          aiRequestIdRef.current = null;
          translationService.pub.clearStream();

          aiTranslationsSubRef.current?.unsubscribe();
          aiTranslationsSubRef.current = translationService.sub
            .getAIContentTranslation()
            .pipe(
              responseHandler({
                errorReturnType: {
                  requestId: '',
                  data: [] as GetWa_AiContentTranslationQueryRes,
                },
              }),
            )
            .subscribe((res) => {
              if (!res.requestId || res.requestId !== aiRequestIdRef.current) {
                return;
              }

              setTranslations((prev) => {
                const updated = prev.map((item) => {
                  const aiTranslation = res.data.find((dt) => dt.languageId === item.languageId);
                  return aiTranslation ? aiTranslation : item;
                });

                stream.emit('recordData', {
                  values: { translations: postprocessTranslations(updated) },
                });

                return updated;
              });

              setAiUpdateKey((prev) => prev + 1);
              setLoading(false);
              translationService.pub.clearStream();
            });
        });

        const closeSub = stream.actionListener('close').subscribe(() => {
          setIsOpened(false);
          setLoading(false);
          aiRequestIdRef.current = null;
          translationService.pub.clearStream();
          aiTranslationsSubRef.current?.unsubscribe();
          aiTranslationsSubRef.current = null;
        });

        return () => {
          openSub.unsubscribe();
          closeSub.unsubscribe();
          aiTranslationsSubRef.current?.unsubscribe();
          aiTranslationsSubRef.current = null;
        };
      }, []);

      const onSaveButtonClick = () => {
        if (!equal(initTranslations, translations)) {
          stream.emit('save', {
            values: { translations: postprocessTranslations(translations) },
          });
        }

        aiRequestIdRef.current = null;
        stream.emit('close');
      };

      return (
        <SWrapper fullWidth stream={stream} popupTitle={t('common.translations')}>
          <ContentLoading loading={loading}>
            <Stack spacing={isRichText || minRows ? 0 : 2}>
              {translations.map((translation, idx) =>
                isRichText ? (
                  <Accordion
                    key={translation.languageId}
                    sx={
                      idx === 0
                        ? { borderTopRightRadius: 4, borderTopLeftRadius: 4 }
                        : idx === translations.length - 1
                        ? {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            borderBottomRightRadius: 4,
                            borderBottomLeftRadius: 4,
                          }
                        : {}
                    }
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      {languages.find((language) => language.id === translation.languageId)?.name &&
                        localizedLabelForBCP47Tag(
                          languages.find((language) => language.id === translation.languageId)
                            ?.name ?? '',
                          i18n.language,
                        )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextEditor
                        key={`${translation.languageId}-${aiUpdateKey}`}
                        defaultValue={translation.content}
                        toolbarItems={richTextToolbarItems}
                        onChange={(v) => {
                          if (v !== undefined) {
                            setTranslations((prev) => {
                              const res = prev.map((item) =>
                                item.languageId === translation.languageId
                                  ? { ...item, content: v ?? '' }
                                  : item,
                              );
                              stream.emit('recordData', {
                                values: { translations: postprocessTranslations(res) },
                              });
                              return res;
                            });
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                ) : minRows ? (
                  <Accordion
                    key={translation.languageId}
                    sx={
                      idx === 0
                        ? { borderTopRightRadius: 4, borderTopLeftRadius: 4 }
                        : idx === translations.length - 1
                        ? {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            borderBottomRightRadius: 4,
                            borderBottomLeftRadius: 4,
                          }
                        : {}
                    }
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      {languages.find((language) => language.id === translation.languageId)?.name &&
                        localizedLabelForBCP47Tag(
                          languages.find((language) => language.id === translation.languageId)
                            ?.name ?? '',
                          i18n.language,
                        )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        key={`${translation.languageId}-${aiUpdateKey}`}
                        value={translation.content}
                        width='100%'
                        multiline
                        minRows={minRows}
                        slotProps={{ htmlInput: maxLength ? { maxLength } : {} }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const { value } = e.target;
                          setTranslations((prev) => {
                            const res = prev.map((item) =>
                              item.languageId === translation.languageId
                                ? { ...item, content: value }
                                : item,
                            );
                            stream.emit('recordData', {
                              values: { translations: postprocessTranslations(res) },
                            });
                            return res;
                          });
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <TextField
                    key={translation.languageId}
                    value={translation.content}
                    label={
                      languages.find((language) => language.id === translation.languageId)?.name &&
                      localizedLabelForBCP47Tag(
                        languages.find((language) => language.id === translation.languageId)
                          ?.name ?? '',
                        i18n.language,
                      )
                    }
                    width='100%'
                    slotProps={{ htmlInput: maxLength ? { maxLength } : {} }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target;
                      setTranslations((prev) => {
                        const res = prev.map((item) =>
                          item.languageId === translation.languageId
                            ? { ...item, content: value }
                            : item,
                        );
                        stream.emit('recordData', {
                          values: { translations: postprocessTranslations(res) },
                        });
                        return res;
                      });
                    }}
                  />
                ),
              )}
            </Stack>
          </ContentLoading>

          <Footer>
            <Button
              variant='outlined'
              disabled={loading || !isOpened}
              startIcon={<AutoAwesomeIcon />}
              onClick={() => {
                const requestId = crypto.randomUUID();
                aiRequestIdRef.current = requestId;
                setLoading(true);

                translationService.pub.getAIContentTranslation({
                  requestId,
                  variables: {
                    params: {
                      contentType,
                      originalContent,
                      targetLanguages: languages.map((lang) => lang.id),
                    },
                  },
                });
              }}
            >
              {t('common.translate_with_ai')}
            </Button>

            <Button variant='contained' onClick={onSaveButtonClick} disabled={loading}>
              {t('common.save')}
            </Button>
          </Footer>
        </SWrapper>
      );
    };
  }
}

export interface ITranslationPopup {
  action: 'save' | 'recordData' | 'reset' | TPopupDefaultActions;
  values?: { translations: ITranslation[] };
  languages?: DictTranslationLanguagesQueryRes;
  maxLength?: number;
  minRows?: number;
  isRichText?: boolean;
  richTextToolbarItems?: TToolbar[];
  contentType?: C_Content_Translation_Type;
  originalContent?: string;
}

export interface ITranslation {
  content: string;
  languageId: string;
}
